import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { uuidv7 } from 'uuidv7';
import { usePageContext } from '../../../../../hooks/PageContext/usePageContext';
import { hashIdentifier } from '../../hashIdentifier';
import { useThanksConfig } from './useThanksConfig';
import { useThanksAnalytics } from './useThanksAnalytics';
import { resolvePageUrl } from '../../willSkipAnalytics';

const THANKS_SCRIPT_URL = 'https://thanks.narvar.com/v1/narvar-widget.js';
const THANKS_INLINE_SCRIPT_URL =
  'https://thanks.narvar.com/v1/narvar-carousel.js';

interface ThanksItem {
  name: string;
  category: string;
  value: number;
  currency: string;
  quantity: number;
}

interface ThanksLocation {
  country?: string;
}

declare global {
  interface Window {
    __thanks?: {
      partnerId: string;
      externalUserId?: string | null;
      traceId?: string;
      embeddedInto: HTMLElement;
      items?: ThanksItem[];
      purchaseLocation?: ThanksLocation;
      customerLocation?: ThanksLocation;
      categories?: string[];
    };
  }
}

interface ThanksProps {
  className?: string;
  layout?: 'INLINE' | 'COVER_IMAGE';
  decisionId?: string;
  isFallback?: boolean;
}

const Thanks: FC<ThanksProps> = ({
  className,
  layout = 'COVER_IMAGE',
  decisionId,
  isFallback,
}) => {
  const {
    state: {
      retailerMoniker,
      orderNumbers,
      deviceType,
      requestUrl,
      versionName,
      localeLanguage,
      localeCountry,
      trackingNumbers,
      trackingStatus,
      sessionId: ctxSessionId,
    },
  } = usePageContext();

  const [sessionId] = useState(() => ctxSessionId ?? uuidv7());

  const {
    partnerId,
    emailHash,
    items,
    purchaseLocation,
    customerLocation,
    categories,
    isLoading,
  } = useThanksConfig({
    retailerMoniker,
    orderNumber: orderNumbers?.[0],
    trackingNumber: trackingNumbers?.[0],
  });

  const [fallbackExternalId, setFallbackExternalId] = useState<string | null>(
    null,
  );
  const fallbackComputed = useRef(false);
  useEffect(() => {
    if (fallbackComputed.current || emailHash) return;
    fallbackComputed.current = true;
    hashIdentifier(orderNumbers?.[0], trackingNumbers?.[0]).then(
      setFallbackExternalId,
    );
  }, [emailHash, orderNumbers, trackingNumbers]);

  const externalUserId = emailHash ?? fallbackExternalId;

  const analyticsParams = useMemo(() => {
    if (!partnerId) return null;
    const locale =
      localeLanguage && localeCountry
        ? `${localeLanguage}_${localeCountry}`
        : 'en_US';
    return {
      retailerMoniker,
      sessionId,
      device: deviceType || ('desktop' as const),
      pageUrl: resolvePageUrl(requestUrl),
      versionName,
      locale,
      orderNumber: orderNumbers?.[0],
      trackingNumber: trackingNumbers?.[0],
      trackingStatus: trackingStatus ?? undefined,
      layout:
        layout === 'COVER_IMAGE' ? ('COVER' as const) : ('INLINE' as const),
      decisionId,
      isFallback,
    };
  }, [
    partnerId,
    retailerMoniker,
    sessionId,
    deviceType,
    requestUrl,
    versionName,
    localeLanguage,
    localeCountry,
    orderNumbers,
    trackingNumbers,
    trackingStatus,
    layout,
    decisionId,
    isFallback,
  ]);

  useThanksAnalytics(analyticsParams);

  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (hasLoaded.current || !containerRef.current || !partnerId) return;
    hasLoaded.current = true;

    window.__thanks = {
      partnerId,
      externalUserId,
      traceId: `${trackingStatus || 'NONE'}|${sessionId}`,
      embeddedInto: containerRef.current,
      items,
      purchaseLocation,
      customerLocation,
      categories,
    };

    const script = document.createElement('script');
    script.src =
      layout === 'INLINE' ? THANKS_INLINE_SCRIPT_URL : THANKS_SCRIPT_URL;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [
    partnerId,
    externalUserId,
    sessionId,
    items,
    purchaseLocation,
    customerLocation,
    categories,
    layout,
  ]);

  if (isLoading || !partnerId) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={className}
      data-test="retailMedia"
      style={
        layout === 'INLINE'
          ? { marginBottom: '0.5rem' }
          : { minHeight: '128px', marginBottom: '0.5rem' }
      }
    />
  );
};

export default Thanks;
